import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, CalendarClock, BadgeCheck, ArrowUpRight, ArrowDownRight, AlertCircle, Activity, PhoneCall, ArrowRightLeft, CheckCircle2, RefreshCw } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import crm, { apiErr } from "./crmApi";
import { useCrm } from "./CrmApp";
import { PageHeader, Card, EmptyState, PriorityBadge, timeAgo, fmtDT, isOverdue } from "./ui";

export const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["last7", "Last 7 days"], ["last30", "Last 30 days"], ["this_month", "This month"]];

function Kpi({ label, value, icon: Icon, children, testid, to }) {
  const body = (
    <div className="crm-card p-4 h-full transition-shadow hover:shadow-md" data-testid={testid}>
      <div className="flex items-center justify-between"><p className="text-[13px] font-medium text-[#5F6E64]">{label}</p><Icon size={16} className="text-[#93A69A]" /></div>
      <p className="text-[26px] font-bold text-[#25352C] mt-1 leading-tight tabular-nums" data-testid={`${testid}-value`}>{value.toLocaleString("en-IN")}</p>
      <div className="text-[12px] mt-1 min-h-[18px]">{children}</div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function Delta({ cur, prev, days }) {
  if (prev == null) return null;
  const span = days === 1 ? "previous day" : `previous ${days} days`;
  if (prev === 0) return <span className="text-[#5F6E64]">0 in the {span}</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const up = pct >= 0;
  return <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? "text-[#047857]" : "text-[#B91C1C]"}`} data-testid="kpi-delta">{up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(pct)}%<span className="font-normal text-[#5F6E64] ml-1">vs {span}</span></span>;
}

export function TrendChart({ trend, height = 220 }) {
  if (!trend.length || trend.every((t) => !t.count)) return <EmptyState icon={Activity} title="No leads in this period" text="New leads will appear here as they are created." />;
  const data = trend.map((t) => ({ ...t, label: new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) }));
  return (
    <div style={{ height }} data-testid="trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
          <defs><linearGradient id="pbsTrend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#236B45" stopOpacity={0.18} /><stop offset="100%" stopColor="#236B45" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke="#E4EBE4" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5F6E64" }} tickLine={false} axisLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#5F6E64" }} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4EBE4", fontSize: 12 }} formatter={(v) => [v, "Leads"]} />
          <Area type="monotone" dataKey="count" stroke="#236B45" strokeWidth={2} fill="url(#pbsTrend)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Bars({ data, color = "#236B45", limit = 7, emptyText = "No data yet" }) {
  const total = data.reduce((a, x) => a + x.count, 0);
  if (!total) return <p className="text-[13px] text-[#5F6E64] py-6 text-center">{emptyText}</p>;
  const max = Math.max(...data.map((x) => x.count));
  return (
    <ul className="space-y-2.5">
      {data.slice(0, limit).map((x) => (
        <li key={x.label} className="text-[13px]">
          <div className="flex justify-between gap-2 mb-1"><span className="truncate text-[#25352C]">{x.label}</span><span className="tabular-nums text-[#5F6E64] shrink-0"><b className="text-[#25352C] font-semibold">{x.count}</b> · {Math.round((x.count / total) * 100)}%</span></div>
          <div className="h-1.5 rounded-full bg-[#F0F5F0] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(x.count / max) * 100}%`, background: color }} /></div>
        </li>
      ))}
      {data.length > limit && <li className="text-[12px] text-[#5F6E64]">+{data.length - limit} more</li>}
    </ul>
  );
}

const ACT_ICON = { interaction: PhoneCall, assign_lead: ArrowRightLeft, complete_task: CheckCircle2, reschedule_task: CalendarClock, create_lead: UserPlus };

function Upcoming({ items }) {
  if (!items.length) return <EmptyState icon={CalendarClock} title="No pending follow-ups" text="Scheduled follow-ups will show here." />;
  return (
    <ul className="divide-y divide-[#E4EBE4]">
      {items.map((t) => {
        const od = isOverdue(t.due_date);
        return (
          <li key={t.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`upcoming-${t.id}`}>
            <div className="min-w-0 flex-1">
              {t.lead_id ? <Link to={`/crm/leads/${t.lead_id}`} className="text-[13px] font-semibold text-[#25352C] hover:text-[#236B45] truncate block">{t.lead_name || t.title}</Link> : <p className="text-[13px] font-semibold truncate">{t.title}</p>}
              <p className="text-[12px] text-[#5F6E64] truncate">{t.owner || "Unassigned"} · <span className={od ? "text-[#B91C1C] font-semibold" : ""}>{od ? "Overdue · " : ""}{fmtDT(t.due_date)}</span></p>
            </div>
            <PriorityBadge p={t.priority} />
          </li>
        );
      })}
    </ul>
  );
}

function Recent({ items }) {
  if (!items.length) return <EmptyState icon={Activity} title="No recent activity" text="Calls, assignments and status changes will appear here." />;
  return (
    <ul className="px-4 py-2">
      {items.map((a, i) => {
        const Icon = ACT_ICON[a.kind] || RefreshCw;
        return (
          <li key={i} className="flex gap-3 py-2">
            <span className="w-7 h-7 rounded-lg bg-[#F0F7F2] text-[#236B45] flex items-center justify-center shrink-0"><Icon size={14} /></span>
            <div className="min-w-0 text-[13px]">
              <p className="text-[#25352C] leading-snug"><span className="font-semibold">{a.by || "System"}</span> <span className="text-[#43544A]">{a.text.charAt(0).toLowerCase() + a.text.slice(1)}</span>{a.lead_id && <> · <Link to={`/crm/leads/${a.lead_id}`} className="text-[#236B45] font-medium hover:underline">{a.lead_name}</Link></>}</p>
              <p className="text-[11px] text-[#5F6E64]">{timeAgo(a.at)}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function CrmDashboard() {
  const { user } = useCrm();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [preset, setPreset] = useState("last30");
  const [employee, setEmployee] = useState("");
  const [nonce, setNonce] = useState(0);
  const [emps, setEmps] = useState([]);
  const canFilter = ["owner", "sales_manager"].includes(user.role);
  useEffect(() => { if (canFilter) crm.employees().then((e) => setEmps(e.filter((x) => x.active !== false))).catch(() => {}); }, [canFilter]);
  useEffect(() => { setD(null); setErr(""); crm.dashboard({ preset, ...(employee && { employee }) }).then(setD).catch((e) => setErr(apiErr(e))); }, [preset, employee, nonce]);
  const K = d?.kpis;
  return (
    <div className="crm-fade">
      <PageHeader title="Dashboard" sub={`Sales overview · ${PRESETS.find(([v]) => v === preset)[1]}${employee ? ` · ${emps.find((e) => e.id === employee)?.name || ""}` : ""}`} actions={<>
        {canFilter && <select value={employee} onChange={(e) => setEmployee(e.target.value)} data-testid="crm-dash-employee" className="crm-in w-auto" aria-label="Team member"><option value="">Entire team</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select>}
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="crm-dash-preset" className="crm-in w-auto" aria-label="Date range">{PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </>} />
      {err ? <div className="crm-card"><EmptyState icon={AlertCircle} title="Couldn't load the dashboard" text={err} action={<button className="crm-btn crm-btn-secondary" data-testid="dash-retry" onClick={() => setNonce((n) => n + 1)}>Retry</button>} /></div> : !d ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="crm-card p-4 h-[106px]"><div className="crm-skel h-3 w-24" /><div className="crm-skel h-7 w-16 mt-3" /></div>)}</div>
      ) : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total Leads" value={K.total} icon={Users} testid="kpi-total-leads" to="/crm/leads"><span className="text-[#5F6E64]">{K.active.toLocaleString("en-IN")} active</span></Kpi>
          <Kpi label="New Leads" value={K.new_in_range} icon={UserPlus} testid="kpi-new-leads"><Delta cur={K.new_in_range} prev={K.new_prev} days={K.period_days} /></Kpi>
          <Kpi label="Follow-ups Due" value={K.followups_due} icon={CalendarClock} testid="kpi-followups-due" to="/crm/tasks">{K.overdue > 0 ? <span className="text-[#B91C1C] font-semibold">{K.overdue} overdue</span> : <span className="text-[#5F6E64]">Nothing overdue</span>}</Kpi>
          <Kpi label="Qualified Leads" value={K.qualified} icon={BadgeCheck} testid="kpi-qualified-leads"><span className="text-[#5F6E64]">{K.total ? `${Math.round((K.qualified / K.total) * 100)}% of all leads` : "—"}</span></Kpi>
        </div>
        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <Card title="Leads Trend" className="lg:col-span-2" testid="trend-card" action={<span className="text-[12px] text-[#5F6E64]">{K.new_in_range} new in period</span>}><TrendChart trend={d.trend} /></Card>
          <Card title="Leads by Status" testid="status-card"><Bars data={d.by_status} /></Card>
        </div>
        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <Card title="Upcoming Follow-ups" bodyClass="" testid="upcoming-card" action={<Link to="/crm/tasks" className="text-[12px] font-semibold text-[#236B45] hover:underline">View all</Link>}><Upcoming items={d.upcoming} /></Card>
          <Card title="Recent Activity" bodyClass="" testid="activity-card"><Recent items={d.activity} /></Card>
          <Card title="Leads by Source" testid="source-card"><Bars data={d.by_source} color="#C9A45C" /></Card>
        </div>
      </>}
    </div>
  );
}
